import { useRef, useState } from "react";
import type { SlotDef, SlotState } from "@ranfac/shared";
import { deleteUpload, uploadSlot } from "../api.js";
import { parseFileForSlot } from "../parseFile.js";

export function UploadSlot({
  period,
  def,
  state,
  onChanged,
}: {
  period: string;
  def: SlotDef;
  state: SlotState;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  async function handleFile(file: File) {
    setBusy(true);
    setErr(null);
    try {
      const parsed = await parseFileForSlot(file, def);
      const res = await uploadSlot(period, def.id, parsed);
      if (!res.ok && res.errors.length > 0) {
        setErr(res.errors.join(" "));
      }
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!confirm(`Remove the uploaded ${def.name}?`)) return;
    setBusy(true);
    try {
      await deleteUpload(period, def.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  const statusClass =
    state.status === "uploaded"
      ? "ok"
      : state.status === "validation_failed"
        ? "err"
        : "missing";

  return (
    <div
      className={`slot ${statusClass} ${dragOver ? "drag" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) void handleFile(f);
      }}
    >
      <div className="slot-head">
        <div>
          <div className="slot-name">{def.name}</div>
          <div className="slot-source">{def.sageSource}</div>
        </div>
        <span className={`status-dot ${statusClass}`} title={state.status} />
      </div>

      <div className="slot-how">{def.howTo}</div>

      {state.status === "uploaded" && (
        <div className="slot-meta ok-line">
          ✓ <strong>{state.filename}</strong> · {state.rowCount} rows ·{" "}
          {relative(state.uploadedAt)} by {short(state.uploadedBy)}
        </div>
      )}

      {state.status === "validation_failed" && (
        <div className="slot-meta err-line">
          ⚠ <strong>{state.filename}</strong> failed validation:{" "}
          {state.errors.join(" ")}
        </div>
      )}

      {err && <div className="slot-meta err-line">{err}</div>}

      <div className="slot-actions">
        <input
          ref={inputRef}
          type="file"
          accept={def.expectedExtensions.map((e) => `.${e}`).join(",")}
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            e.target.value = "";
          }}
        />
        <button
          className="secondary"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy
            ? "Working…"
            : state.status === "uploaded"
              ? "Replace file"
              : "Upload file"}
        </button>
        {state.status === "uploaded" && (
          <button className="link-btn" disabled={busy} onClick={onDelete}>
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

function short(email: string | null): string {
  if (!email) return "—";
  return email.split("@")[0] ?? email;
}

function relative(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso.replace(" ", "T") + "Z");
  const delta = (Date.now() - d.getTime()) / 1000;
  if (delta < 60) return "just now";
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  return d.toLocaleDateString();
}
